import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { B2bVehicleReturnEvent } from '../../../models/Contract';
import { ContractsService } from '../../../services/contracts.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { B2bContractPlateOption } from '../b2b-contract-plate-select/b2b-contract-plate-select.component';
import { exportB2bTableToExcel } from '../../../utils/b2b-table-export.util';

@Component({
  selector: 'app-b2b-return-events',
  templateUrl: './b2b-return-events.component.html',
  styleUrls: ['../shared/b2b-operational-shared.css', './b2b-return-events.component.css']
})
export class B2bReturnEventsComponent implements OnInit {
  contractId: number | null = null;
  contractNumber = '';
  contractName = '';
  items: B2bVehicleReturnEvent[] = [];
  isLoading = false;
  submitting = false;
  showModal = false;
  viewMode = false;
  editingId: number | null = null;
  selectedVehicleId: number | null = null;
  form!: FormGroup;

  columns = [
    { titulo: 'Placa', label: 'plate' },
    { titulo: 'Retorno à base', label: 'returnToBaseDate', formatter: (v: any) => this.formatDate(v) },
    { titulo: 'Recolocação', label: 'redeployDate', formatter: (v: any) => this.formatDate(v) },
    { titulo: 'Observações', label: 'notes' }
  ];

  actions: TableAction[] = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view' },
    { label: 'Editar', icon: 'fas fa-edit', action: 'edit', class: 'btn-edit' },
    { label: 'Excluir', icon: 'fas fa-trash', action: 'delete', class: 'btn-delete' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      plate: [''],
      returnToBaseDate: [''],
      redeployDate: [''],
      notes: ['']
    });

    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.loadContractInfo();
        this.loadItems();
      }
    });
  }

  get modalTitle(): string {
    if (this.viewMode) return 'Visualizar retorno';
    return this.editingId ? 'Editar retorno' : 'Novo retorno';
  }

  loadContractInfo(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (c) => {
        this.contractNumber = c.contractNumber || '';
        this.contractName = c.contractName || '';
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  loadItems(): void {
    if (!this.contractId) return;
    this.isLoading = true;
    this.contractsService.getB2bReturnEvents(this.contractId).subscribe({
      next: (list) => {
        this.items = (list || []).map(x => this.normalize(x));
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  openCreate(): void {
    this.viewMode = false;
    this.editingId = null;
    this.selectedVehicleId = null;
    this.form.enable();
    this.form.reset({ plate: '', returnToBaseDate: '', redeployDate: '', notes: '' });
    this.showModal = true;
  }

  openView(item: B2bVehicleReturnEvent): void {
    this.patchForm(item);
    this.viewMode = true;
    this.form.disable();
    this.showModal = true;
  }

  openEdit(item: B2bVehicleReturnEvent): void {
    this.patchForm(item);
    this.viewMode = false;
    this.form.enable();
    this.showModal = true;
  }

  private patchForm(item: B2bVehicleReturnEvent): void {
    this.editingId = item.id ?? null;
    this.selectedVehicleId = item.vehicleId ?? null;
    this.form.patchValue({
      plate: item.plate || '',
      returnToBaseDate: this.toDateInput(item.returnToBaseDate),
      redeployDate: this.toDateInput(item.redeployDate),
      notes: item.notes || ''
    });
  }

  onPlateSelected(option: B2bContractPlateOption | null): void {
    this.selectedVehicleId = option?.vehicleId ?? null;
  }

  closeModal(): void {
    this.showModal = false;
    this.viewMode = false;
    this.editingId = null;
    this.selectedVehicleId = null;
    this.form.enable();
  }

  submit(): void {
    if (this.viewMode || !this.contractId || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const payload: B2bVehicleReturnEvent = {
      b2bContractId: this.contractId,
      plate: raw.plate || null,
      vehicleId: this.selectedVehicleId,
      returnToBaseDate: raw.returnToBaseDate ? new Date(raw.returnToBaseDate).toISOString() : null,
      redeployDate: raw.redeployDate ? new Date(raw.redeployDate).toISOString() : null,
      notes: raw.notes || null
    };

    this.submitting = true;
    const req$ = this.editingId
      ? this.contractsService.updateB2bReturnEvent(this.contractId, this.editingId, { ...payload, id: this.editingId })
      : this.contractsService.createB2bReturnEvent(this.contractId, payload);

    req$.subscribe({
      next: () => {
        this.submitting = false;
        this.errorService.showSuccessMessage(this.editingId ? 'Retorno atualizado.' : 'Retorno criado.');
        this.closeModal();
        this.loadItems();
      },
      error: () => { this.submitting = false; }
    });
  }

  onActionClick(event: { action: string; item: B2bVehicleReturnEvent }): void {
    if (event.action === 'view') this.openView(event.item);
    if (event.action === 'edit') this.openEdit(event.item);
    if (event.action === 'delete') this.deleteItem(event.item);
  }

  deleteItem(item: B2bVehicleReturnEvent): void {
    if (!this.contractId || !item.id) return;
    this.confirmationService.confirmDelete(item.plate || 'este retorno', 'o retorno').subscribe({
      next: (ok) => {
        if (!ok) return;
        this.contractsService.deleteB2bReturnEvent(this.contractId!, item.id!).subscribe({
          next: () => {
            this.errorService.showSuccessMessage('Retorno excluído.');
            this.loadItems();
          }
        });
      }
    });
  }


  exportExcel(): void {
    exportB2bTableToExcel(
      `b2b-retornos-${this.contractNumber || this.contractId}`,
      this.columns,
      this.items,
      'Retornos'
    );
  }

  navigateBack(): void {
    if (this.contractId) this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    else this.router.navigate(['/b2b/contracts']);
  }

  private normalize(dto: any): B2bVehicleReturnEvent {
    return {
      id: dto.id ?? dto.Id,
      b2bContractId: dto.b2bContractId ?? dto.b2BContractId ?? dto.B2BContractId ?? this.contractId!,
      plate: dto.plate ?? dto.Plate ?? dto.vehiclePlate ?? dto.VehiclePlate ?? null,
      vehicleId: dto.vehicleId ?? dto.VehicleId ?? null,
      returnToBaseDate: dto.returnToBaseDate ?? dto.ReturnToBaseDate ?? null,
      redeployDate: dto.redeployDate ?? dto.RedeployDate ?? null,
      notes: dto.notes ?? dto.Notes ?? null
    };
  }

  private toDateInput(value: any): string {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  }

  formatDate(value: any): string {
    if (!value) return '—';
    try { return new Date(value).toLocaleDateString('pt-BR'); } catch { return String(value); }
  }
}
